import { useEffect, useState } from 'react';
import { hasScope } from '@nyce/admin-kit/api';
import { SCOPES } from '../../api';
import { useHubLogin } from '../../session';
import { loadTargets, type AuthTargetInfo } from '../../identity/session';
import type { Issue } from '../../pages/dashboard/shared';
import {
  allJobs,
  printQueue,
  type JobStatus,
  type PrintJob,
  type Printer,
  type PrintServer,
  type PrintUsage,
  type TokenInfo,
  type UsageTotals,
} from '../api/client';
import { STUCK_MS, isStuck, pqPath, printServersOrNull, serverKey, usageOrNull } from '../shared';
import { formatRelative } from '../../lib/format';

// The print queue per environment, for the Print queue group dashboard and the main dashboard. The print queue has no
// counting endpoint: unfinished and failed jobs are read in full (keyset pages, up to 5000), the newest 1000 of any
// status give the recent volume (printed jobs are deleted after a while anyway). History comes from the print queue's
// usage counters (1.7.0, `docs/printqueue-usage.md`): the last USAGE_DAYS days per tenant, read once and cut per range.

export interface TenantRow {
  tenantId: string;
  servers: string[];
  printers: number;
  open: number;
  errors: number;
  stuck: number;
  lastJob: string | null;
  /** From the usage counters (null before print queue 1.7.0). */
  printedMonth: number | null;
  printedMonthBytes: number | null;
}

export interface PrintQueueSummary {
  target: AuthTargetInfo;
  /** Why the environment could not be read at all. */
  error: string | null;
  /** Every job not printed yet or failed. */
  open: PrintJob[];
  /** More unfinished jobs exist than were read. */
  openTruncated: boolean;
  /** The newest jobs of any status (≤ 1000). */
  recent: PrintJob[];
  printers: Printer[] | null;
  /** Print servers with their state; null before print queue 1.5.0 (or when unreadable). */
  printServers: PrintServer[] | null;
  tokens: TokenInfo[] | null;
  /** Usage per day and tenant over the last USAGE_DAYS days; null before print queue 1.7.0 (or when unreadable). */
  usage: PrintUsage | null;
  partErrors: string[];
}

export const RECENT = 1000;
/** Days of usage read per environment: the longest chart range. */
export const USAGE_DAYS = 90;
export const USAGE_RANGES = [14, 30, 90] as const;
export type UsageRange = (typeof USAGE_RANGES)[number];

async function summarize(t: AuthTargetInfo): Promise<PrintQueueSummary> {
  const api = printQueue(t.id);
  const base: PrintQueueSummary = {
    target: t,
    error: null,
    open: [],
    openTruncated: false,
    recent: [],
    printers: null,
    printServers: null,
    tokens: null,
    usage: null,
    partErrors: [],
  };
  const partErrors: string[] = [];
  const soft = <T>(p: Promise<T>, what: string) =>
    p.catch((e: Error) => {
      partErrors.push(`${what}: ${e.message}`);
      return null;
    });
  let open: Awaited<ReturnType<typeof allJobs>>;
  try {
    open = await allJobs(api, { statuses: ['Registered', 'Fetched', 'Printing', 'Error'] });
  } catch (e) {
    return { ...base, error: (e as Error).message };
  }
  const [recent, printers, printServers, tokens, usage] = await Promise.all([
    soft(api.jobs({ size: RECENT }), 'recent jobs'),
    soft(api.printers(), 'printers'),
    soft(printServersOrNull(api), 'print servers'),
    soft(api.tokens(), 'tokens'),
    // `to` = today in the print queue's zone; a day more here only adds an empty day
    soft(usageOrNull(api, { from: dayKey(new Date(Date.now() - USAGE_DAYS * 86_400_000)) }), 'usage'),
  ]);
  return {
    ...base,
    open: open.jobs,
    openTruncated: open.more,
    recent: recent ?? [],
    printers,
    printServers,
    tokens,
    usage,
    partErrors,
  };
}

const dayKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** `yyyy-MM-dd` + n days (calendar arithmetic, no time zone). */
const addDays = (day: string, n: number) =>
  new Date(Date.parse(`${day}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

const ZERO: UsageTotals = { added: 0, addedBytes: 0, printed: 0, printedBytes: 0, failed: 0 };
const add = (a: UsageTotals, b: UsageTotals): UsageTotals => ({
  added: a.added + b.added,
  addedBytes: a.addedBytes + b.addedBytes,
  printed: a.printed + b.printed,
  printedBytes: a.printedBytes + b.printedBytes,
  failed: a.failed + b.failed,
});

/** Usage totals over the days from `fromDay` (inclusive) to the end of the usage. */
export function usageSince(u: PrintUsage, fromDay: string): UsageTotals {
  return u.rows.filter((r) => r.period >= fromDay).reduce(add, ZERO);
}

/** The first and last day of the last `days` days of the usage (its `to` = today in the print queue's zone). */
export function usageWindow(u: PrintUsage, days: number) {
  return { from: addDays(u.to, 1 - days), to: u.to, today: u.to, monthStart: `${u.to.slice(0, 7)}-01` };
}

/** Jobs printed per day over the last `days` days of the usage, every day present (days in the usage's zone). */
export function printedPerDay(u: PrintUsage, days: number) {
  const counts = new Map<string, number>();
  const { from } = usageWindow(u, days);
  for (let i = 0; i < days; i++) counts.set(addDays(from, i), 0);
  for (const r of u.rows) if (counts.has(r.period)) counts.set(r.period, counts.get(r.period)! + r.printed);
  return [...counts.entries()].map(([k, v]) => ({ key: k, label: k, v }));
}

export const countBy = (jobs: PrintJob[]) => {
  const c: Record<JobStatus, number> = { Registered: 0, Fetched: 0, Printing: 0, Printed: 0, Error: 0 };
  for (const j of jobs) c[j.status] = (c[j.status] ?? 0) + 1;
  return c;
};

/** Per tenant: print servers, printers, unfinished / failed / stuck jobs, the newest job. Busiest first. */
export function tenantRows(s: PrintQueueSummary, now = Date.now()): TenantRow[] {
  const rows = new Map<string, TenantRow>();
  const row = (id: string) => {
    let r = rows.get(id);
    if (!r)
      rows.set(
        id,
        (r = {
          tenantId: id,
          servers: [],
          printers: 0,
          open: 0,
          errors: 0,
          stuck: 0,
          lastJob: null,
          printedMonth: s.usage ? 0 : null,
          printedMonthBytes: s.usage ? 0 : null,
        })
      );
    return r;
  };
  for (const p of s.printers ?? []) {
    const r = row(p.tenantId);
    r.printers++;
    if (!r.servers.includes(p.printServerName)) r.servers.push(p.printServerName);
  }
  for (const j of s.open) {
    const r = row(j.tenantId);
    if (j.status === 'Error') r.errors++;
    else r.open++;
    if (isStuck(j, now)) r.stuck++;
  }
  for (const j of [...s.recent, ...s.open]) {
    const r = row(j.tenantId);
    if (j.dateAdded && (!r.lastJob || j.dateAdded > r.lastJob)) r.lastJob = j.dateAdded;
  }
  if (s.usage) {
    const { monthStart } = usageWindow(s.usage, 1);
    for (const u of s.usage.rows) {
      if (u.period < monthStart || !u.printed) continue;
      const r = row(u.tenantId);
      r.printedMonth! += u.printed;
      r.printedMonthBytes! += u.printedBytes;
    }
  }
  return [...rows.values()].sort(
    (a, b) =>
      b.errors + b.stuck - (a.errors + a.stuck) || b.open - a.open || a.tenantId.localeCompare(b.tenantId)
  );
}

/** Jobs added per day (local dates) over the last `days`, from the recent page (print queues before 1.7.0). */
export function jobsPerDay(jobs: PrintJob[], days = 14, now = new Date()) {
  const counts = new Map<string, number>();
  for (let i = days - 1; i >= 0; i--) counts.set(dayKey(new Date(now.getTime() - i * 86_400_000)), 0);
  for (const j of jobs) {
    if (!j.dateAdded) continue;
    const k = dayKey(new Date(j.dateAdded));
    if (counts.has(k)) counts.set(k, counts.get(k)! + 1);
  }
  return [...counts.entries()].map(([k, v]) => ({ key: k, label: k, v }));
}

/** What needs a look: failed jobs and stuck ones per print server, tokens about to expire. */
export function printQueueIssues(list: PrintQueueSummary[], now = Date.now()): Issue[] {
  const out: Issue[] = [];
  for (const s of list) {
    const env = s.target.label;
    const link = (path: string) => pqPath(path, s.target.id);
    if (s.error) {
      out.push({
        key: `pq:${s.target.id}`,
        status: 'error',
        name: `Print queue ${env}`,
        what: 'environment',
        detail: s.error,
        to: link('/printqueue'),
      });
      continue;
    }
    const byServer = new Map<string, { tenantId: string; server: string; errors: number; stuck: number }>();
    for (const j of s.open) {
      const k = serverKey(j.tenantId, j.printServerName);
      const e = byServer.get(k) ?? { tenantId: j.tenantId, server: j.printServerName, errors: 0, stuck: 0 };
      if (j.status === 'Error') e.errors++;
      else if (isStuck(j, now)) e.stuck++;
      byServer.set(k, e);
    }
    // Print queue 1.5.0+ knows each print server's state: stuck jobs at a print server that is still in contact are
    // its printers' problem (degraded), an offline one is down. Before 1.5.0 a job it printed within STUCK_MS (from
    // the recent page) shows it is alive.
    const servers = s.printServers && new Map(s.printServers.map((p) => [serverKey(p.tenantId, p.name), p]));
    const printedAt = new Map<string, string>();
    if (!servers)
      for (const j of s.recent) {
        const k = serverKey(j.tenantId, j.printServerName);
        const at = j.modificationDate ?? j.dateAdded;
        if (j.status === 'Printed' && at && at > (printedAt.get(k) ?? '')) printedAt.set(k, at);
      }
    const serverLink = (tenantId: string, name: string) =>
      link(
        `/printqueue/printserver?tenant=${encodeURIComponent(tenantId)}&server=${encodeURIComponent(name)}`
      );
    for (const e of byServer.values()) {
      const q = `tenant=${encodeURIComponent(e.tenantId)}&server=${encodeURIComponent(e.server)}`;
      const k = serverKey(e.tenantId, e.server);
      const info = servers?.get(k);
      const printed = printedAt.get(k);
      const stuck = `${e.stuck} job${e.stuck === 1 ? '' : 's'} unfinished for more than 15 min`;
      const issue = (status: string, detail: string) =>
        out.push({
          key: `pq:${s.target.id}:stuck:${e.tenantId}:${e.server}`,
          status,
          name: `${e.tenantId} · ${e.server}`,
          what: `print server · ${env}`,
          detail,
          to: link(`/printqueue/jobs?${q}&status=stuck`),
        });
      if (!e.stuck || info?.state === 'offline') {
        // an offline print server is reported below, once
      } else if (info)
        issue(
          'warn',
          `${stuck} although the print server is ${info.state === 'live' ? 'connected' : 'polling'}: check its printers`
        );
      else if (servers) issue('down', `${stuck}, never seen by the print queue`);
      else if (printed && now - Date.parse(printed) <= STUCK_MS)
        issue(
          'warn',
          `${stuck} although the print server printed ${formatRelative(printed)}: check its printers`
        );
      else
        issue(
          'down',
          `${stuck}, ${printed ? `last printed ${formatRelative(printed)}` : 'nothing printed recently'} (is the print server polling?)`
        );
      if (e.errors)
        out.push({
          key: `pq:${s.target.id}:errors:${e.tenantId}:${e.server}`,
          status: 'warn',
          name: `${e.tenantId} · ${e.server}`,
          what: `print server · ${env}`,
          detail: `${e.errors} failed job${e.errors === 1 ? '' : 's'}`,
          to: link(`/printqueue/jobs?${q}&status=Error`),
        });
    }
    for (const p of s.printServers ?? []) {
      if (p.state !== 'offline' || p.openJobs === 0) continue;
      const stuck = byServer.get(serverKey(p.tenantId, p.name))?.stuck ?? 0;
      out.push({
        key: `pq:${s.target.id}:offline:${p.tenantId}:${p.name}`,
        status: 'down',
        name: `${p.tenantId} · ${p.name}`,
        what: `print server · ${env}`,
        detail:
          `offline (${p.lastSeenAt ? `last seen ${formatRelative(p.lastSeenAt)}` : 'not seen yet'}) with ` +
          `${p.openJobs} unfinished job${p.openJobs === 1 ? '' : 's'}` +
          (stuck ? `, ${stuck} for more than 15 min` : ''),
        to: serverLink(p.tenantId, p.name),
      });
    }
    for (const t of s.tokens ?? []) {
      if (!t.expires || t.role === 'Admin') continue;
      const left = new Date(t.expires).getTime() - now;
      if (left > 0 && left < 30 * 86_400_000)
        out.push({
          key: `pq:${s.target.id}:token:${t.id}`,
          status: 'warn',
          name: `${t.name ?? 'token'} (${t.tenantId ?? '?'})`,
          what: `${t.role ?? ''} token · ${env}`,
          detail: `expires in ${Math.ceil(left / 86_400_000)} days`,
          to: link(`/printqueue/tokens?tenant=${encodeURIComponent(t.tenantId ?? '')}`),
        });
    }
  }
  return out;
}

const TTL = 60_000;
let cache: { at: number; data: Promise<PrintQueueSummary[]> } | null = null;

/** Every environment with a print queue (callers with ophub:printqueue:read), cached for a minute across dashboards. */
export function usePrintQueueSummary() {
  const enabled = hasScope(useHubLogin()?.scopes, SCOPES.printqueueRead);
  const [state, setState] = useState<{
    data: PrintQueueSummary[] | null;
    loading: boolean;
    error: string | null;
  }>({
    data: null,
    loading: enabled,
    error: null,
  });
  const [round, setRound] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    if (round > 0 || !cache || Date.now() - cache.at > TTL)
      cache = {
        at: Date.now(),
        data: loadTargets().then((list) => Promise.all(list.filter((t) => t.printQueue).map(summarize))),
      };
    cache.data.then(
      (data) => alive && setState({ data, loading: false, error: null }),
      (e) => alive && setState({ data: null, loading: false, error: `Print queue: ${(e as Error).message}` })
    );
    return () => {
      alive = false;
    };
  }, [enabled, round]);
  return { ...state, enabled, reload: () => setRound((r) => r + 1) };
}

/** Test hook. */
export const resetPrintQueueSummary = () => {
  cache = null;
};
